import React, { useState } from 'react';
import {
    CalendarDays,
    Ellipsis,
    Eye,
    FolderOpen,
    Loader2,
    PenLine,
    Trash2,
} from 'lucide-react';
import { Button } from '../../ui/button';
import { Badge } from '../../ui/badge';
import { Card, CardContent } from '../../ui/card';
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
} from '../../ui/dialog';
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from '../../ui/alert-dialog';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
} from '../../ui/dropdown-menu';
import { DocumentSchema } from '../../documents/types';
import { CptReportPreview } from '../cpt/CptReportPreview';
import { PreviewRenderDocument } from '../cpt/utils/previewMergeUtils';
import { CaseReport } from '../../../services/reportService';
import { cn } from '../../../lib/utils';
import {
    getCaseContextPreviewDocument,
    getCaseDateMeta,
    getCaseEligibilityStatuses,
    getCaseStatusMeta,
    getCaseSummaryParts,
    getDeleteState,
    getPrimaryPreviewDocument,
    getRiskBenefitDocument,
} from './manageReportsCardUtils';

interface ManageReportsCaseCardProps {
    caseItem: CaseReport;
    dataSources: any;
    schemaMap: Record<string, DocumentSchema>;
    isDeleting: boolean;
    onDeleteCase: (caseRecordId: string) => Promise<void>;
    onOpenCase: (caseItem: CaseReport, schemaKey?: string) => void;
}

const CPT_BADGE_TEXT_CLASS = 'text-[#6AA2FF] border-[#6AA2FF]';

const ManageReportsCaseCard: React.FC<ManageReportsCaseCardProps> = ({
    caseItem,
    dataSources,
    schemaMap,
    isDeleting,
    onDeleteCase,
    onOpenCase,
}) => {
    const [isPreviewOpen, setIsPreviewOpen] = useState(false);
    const [showDeleteDialog, setShowDeleteDialog] = useState(false);

    const caseContextDocument = getCaseContextPreviewDocument(caseItem);
    const previewDocument = getPrimaryPreviewDocument(caseItem);
    const riskBenefitDocument = getRiskBenefitDocument(caseItem);
    const { exam, clinicalIndication } = getCaseSummaryParts(caseItem);
    const { scanDateLabel, updatedAtLabel } = getCaseDateMeta(caseItem);
    const codeStatuses = getCaseEligibilityStatuses(caseItem);
    const caseStatus = getCaseStatusMeta(caseItem);
    const deleteState = getDeleteState(caseItem);

    const previewDocuments: PreviewRenderDocument[] = [
        caseContextDocument,
        previewDocument,
        riskBenefitDocument,
    ]
        .filter(Boolean)
        .filter(
            (doc, i, arr) =>
                arr.findIndex((d) => d?.schemaKey === doc?.schemaKey) === i
        )
        .map((document) => ({
            schema:
                (document?.document?.schema as DocumentSchema) ||
                schemaMap[document!.schemaKey] ||
                ({
                    id: 'fallback',
                    title: document?.title || 'CPT Report',
                    description: '',
                    sections: [],
                } as DocumentSchema),
            data: document!.data,
            sourceKey: document!.schemaKey,
            sourceLabel: document!.title,
        }));

    return (
        <>
            <Card>
                <CardContent className="p-5 md:p-6">
                    <div className="flex flex-col gap-5 lg:flex-row lg:items-start lg:gap-8">
                        {/* Left content section */}
                        <div className="min-w-0 flex-1 space-y-2">
                            <div className="flex flex-wrap items-center gap-2 text-sm text-muted-foreground">
                                <Badge
                                    variant="outline"
                                    className={cn(
                                        caseStatus.className,
                                        'gap-1.5'
                                    )}
                                >
                                    <span className="inline-block h-1.5 w-1.5 rounded-full bg-current" />
                                    {caseStatus.label}
                                </Badge>
                                {caseItem.needsMySignature && (
                                    <Badge
                                        variant="outline"
                                        className="gap-1.5 border-amber-500/40 bg-amber-500/10 text-amber-700 dark:text-amber-300"
                                    >
                                        <PenLine className="h-3 w-3" />
                                        Needs your signature
                                    </Badge>
                                )}
                                <span>{caseItem.caseId}</span>
                                <span className="text-muted-foreground/50">
                                    &middot;
                                </span>
                                <span>Updated {updatedAtLabel}</span>
                            </div>

                            <h3 className="text-base font-semibold leading-tight md:text-lg">
                                {exam || 'No exam documented'}
                            </h3>

                            {clinicalIndication && (
                                <p className="text-sm text-muted-foreground">
                                    {clinicalIndication}
                                </p>
                            )}

                            {codeStatuses.length > 0 && (
                                <div className="flex flex-wrap items-center gap-2 pt-1">
                                    {codeStatuses.map((code) => (
                                        <Badge
                                            key={code}
                                            variant="outline"
                                            className={CPT_BADGE_TEXT_CLASS}
                                        >
                                            {code}
                                        </Badge>
                                    ))}
                                </div>
                            )}
                        </div>

                        {/* Right actions section */}
                        <div className="flex flex-col items-start gap-3 lg:shrink-0 lg:items-end">
                            <Card className="border-border/50 bg-[#fafafa] dark:bg-[#1a1a1a] dark:border-white/10">
                                <CardContent className="space-y-0.5 p-3">
                                    <div className="flex items-center gap-1.5 text-[10px] font-medium uppercase tracking-wider text-black/70 dark:text-white/70">
                                        <CalendarDays className="h-3 w-3" />
                                        Scan date
                                    </div>
                                    <div className="text-sm font-medium leading-tight text-black dark:text-white">
                                        {scanDateLabel}
                                    </div>
                                </CardContent>
                            </Card>

                            <div className="flex items-center gap-2">
                                {previewDocument && (
                                    <Button
                                        variant="outline"
                                        size="sm"
                                        onClick={() => setIsPreviewOpen(true)}
                                    >
                                        <Eye className="h-4 w-4" />
                                        Preview
                                    </Button>
                                )}

                                <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={() => onOpenCase(caseItem)}
                                >
                                    <FolderOpen className="h-4 w-4" />
                                    View / Edit case
                                </Button>

                                <DropdownMenu>
                                    <DropdownMenuTrigger asChild>
                                        <Button
                                            variant="outline"
                                            size="icon"
                                            className="h-8 w-8"
                                        >
                                            <Ellipsis className="h-4 w-4" />
                                            <span className="sr-only">
                                                More actions
                                            </span>
                                        </Button>
                                    </DropdownMenuTrigger>
                                    <DropdownMenuContent align="end">
                                        <DropdownMenuItem
                                            className="text-destructive focus:text-destructive"
                                            disabled={
                                                !deleteState.canDelete ||
                                                isDeleting
                                            }
                                            onClick={() =>
                                                setShowDeleteDialog(true)
                                            }
                                        >
                                            {isDeleting ? (
                                                <Loader2 className="h-4 w-4 animate-spin" />
                                            ) : (
                                                <Trash2 className="h-4 w-4" />
                                            )}
                                            Delete Case
                                        </DropdownMenuItem>
                                    </DropdownMenuContent>
                                </DropdownMenu>
                            </div>
                        </div>
                    </div>
                </CardContent>
            </Card>

            {/* Preview dialog */}
            {previewDocument && (
                <Dialog open={isPreviewOpen} onOpenChange={setIsPreviewOpen}>
                    <DialogContent className="flex max-h-[90vh] max-w-4xl flex-col gap-0 overflow-hidden p-0">
                        <DialogHeader className="shrink-0 border-b px-6 pb-4 pt-6 pr-14">
                            <DialogTitle>Case Preview</DialogTitle>
                        </DialogHeader>
                        <CptReportPreview
                            className="min-h-0 flex-1"
                            documents={previewDocuments}
                            applicableCodes={caseItem.cptEligibleCodes}
                            dataSources={dataSources}
                            caseId={caseItem.caseId}
                            onClose={() => setIsPreviewOpen(false)}
                        />
                    </DialogContent>
                </Dialog>
            )}

            {/* Delete confirmation dialog */}
            <AlertDialog
                open={showDeleteDialog}
                onOpenChange={setShowDeleteDialog}
            >
                <AlertDialogContent>
                    <AlertDialogHeader>
                        <AlertDialogTitle>Delete this case?</AlertDialogTitle>
                        <AlertDialogDescription>
                            This permanently removes the case and its draft
                            report documents.
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel>Cancel</AlertDialogCancel>
                        <AlertDialogAction asChild>
                            <Button
                                variant="destructive"
                                onClick={() => onDeleteCase(caseItem._id)}
                            >
                                Delete case
                            </Button>
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </>
    );
};

export default ManageReportsCaseCard;
